import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useInAppNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from './api';

/**
 * Dzwoneczek powiadomień w topbar — Faza 10.
 *
 * Polling co 60 s. Po kliknięciu otwiera dropdown z listą; pojedyncze
 * kliknięcie pozycji oznacza ją jako przeczytaną. „Oznacz wszystkie" w stopce.
 *
 * Dostępne dla wszystkich zalogowanych. Backend filtruje wg `userId`,
 * więc każdy widzi tylko swoje powiadomienia (zgodnie z matrycą RBAC F10).
 */
export function NotificationBell(): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useInAppNotifications(false, 60_000);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const items = data?.items ?? [];
  const unread = data?.unreadCount ?? 0;

  function formatTime(iso: string): string {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
        aria-label={t('notifications.toggle')}
      >
        {/* Bell icon (SVG inline, brak zewnętrznych zależności). */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-96 max-w-[calc(100vw-2rem)] origin-top-right rounded-lg border border-zinc-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
            <span className="text-sm font-semibold text-zinc-900">
              {t('notifications.title')}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-xs text-brand-600 hover:text-brand-700 disabled:opacity-50"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && (
              <div className="px-4 py-6 text-center text-sm text-zinc-500">
                {t('common.loading')}
              </div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-zinc-500">
                {t('notifications.empty')}
              </div>
            )}
            {!isLoading &&
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.read) markRead.mutate(n.id);
                  }}
                  className={`flex w-full flex-col gap-0.5 border-b border-zinc-100 px-4 py-3 text-left transition hover:bg-zinc-50 ${
                    n.read ? '' : 'bg-brand-50/40'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span
                        aria-hidden
                        className="mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-brand-500"
                      />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-zinc-900">{n.title}</div>
                      <div className="mt-0.5 text-xs text-zinc-600">{n.body}</div>
                      <div className="mt-1 text-[10px] uppercase tracking-wide text-zinc-400">
                        {formatTime(n.createdAt)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
